/* import __COLOCATED_TEMPLATE__ from './configuration-selector.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type StandardizedPlatform from 'embercom/objects/standalone/platforms/standardized-platform';
import type AppService from 'embercom/services/app-service';
import { StandalonePlatform } from 'embercom/objects/standalone/constants';
import type StandaloneSalesforceConfiguration from 'embercom/models/standalone/salesforce-configuration';

interface Signature {
  Args: {
    platform: StandardizedPlatform<StandaloneSalesforceConfiguration>;
    requireConversationSyncing?: boolean;
    redirectLocation?: string;
  };
}

export default class ConfigurationSelector extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: AppService;

  get isLoading() {
    if (!this.appService.app.canUseStandaloneMultiEnv) {
      return false;
    }

    return (
      this.platform.configurations === undefined || this.platform.activeConfiguration === undefined
    );
  }

  get platform() {
    return this.args.platform;
  }

  get configurations() {
    return this.platform.configurations ?? [];
  }

  get activeConfiguration() {
    return this.platform.activeConfiguration;
  }

  get showSelector() {
    return (
      !this.isLoading &&
      this.appService.app.canUseStandaloneMultiEnv &&
      (this.configurations.length > 1 || this.activeConfiguration?.isConnected)
    );
  }

  get configurationOptions() {
    return this.configurations.map((config) => ({
      value: config.id,
      text: this.formatLabel(config),
    }));
  }

  formatLabel(config: StandaloneSalesforceConfiguration): string {
    if (this.platform.platformKey === StandalonePlatform.Salesforce) {
      return `${config?.environmentLabel} - ${config?.domain}`;
    }

    return this.intl.t('standalone.salesforce.configuration-selector.unknown');
  }

  @action
  selectConfiguration(configId: string) {
    let config = this.configurations.find((c) => c.id === configId);
    if (config) {
      this.platform.switchToConfiguration(config);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::ConfigurationSelector': typeof ConfigurationSelector;
    'standalone/configuration-selector': typeof ConfigurationSelector;
  }
}
